import type { CampaignStatus } from "../../schemas/campaignSchema";

/**
 * One badge vocabulary for both halves of a campaign.
 *
 * A campaign row shows the CREATIVE's flight window; a placement row shows that
 * placement's SCHEDULE window. They are different questions with the same three
 * answers, so each status carries both wordings rather than two lookup tables
 * that can drift apart. Colours match CREATIVE_STATUS_BADGE so the creatives
 * list and the campaigns list read as one portal.
 */
export const CAMPAIGN_STATUS_BADGE: Record<
  CampaignStatus,
  { label: string; scheduleLabel: string; className: string }
> = {
  ACTIVE: {
    label: "Active",
    scheduleLabel: "Running",
    className: "bg-success-100 text-success-700",
  },
  UPCOMING: {
    label: "Upcoming",
    scheduleLabel: "Upcoming",
    className: "bg-blue-100 text-blue-700",
  },
  EXPIRED: {
    label: "Ended",
    scheduleLabel: "Finished",
    className: "bg-gray-100 text-gray-600",
  },
};

const UPLOAD_ID_PREFIX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[-_]/i;
const FILE_EXTENSION = /\.[a-z0-9]{2,4}$/i;

/**
 * A creative's name as something a person can read.
 *
 * Uploads are stored under their original filename, which usually arrives with
 * the upload's UUID glued to the front — "c3003e2c-…-3322fe-Vid3.mp4". Shown
 * raw that is 45 characters of noise before the two that identify the ad, so
 * the id and the extension come off for display. The full name is still worth
 * keeping in a `title` so nothing is actually hidden.
 */
export const displayCreativeName = (name: string): string => {
  const readable = name.replace(UPLOAD_ID_PREFIX, "").replace(FILE_EXTENSION, "");
  return readable.trim() || name;
};

const DAY_ORDER = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

/**
 * Days are stored as whatever the scheduler wrote, so they are normalised for
 * display rather than assumed: uppercased, truncated to three letters, and put
 * back in week order. "Every day" is friendlier than listing all seven.
 */
export const formatDays = (days?: string[] | null): string => {
  if (!days || days.length === 0) return "Every day";
  const normalised = days.map((day) => day.slice(0, 3).toUpperCase());
  const ordered = DAY_ORDER.filter((day) => normalised.includes(day));
  if (ordered.length === 7) return "Every day";
  return (ordered.length ? ordered : normalised).join(", ");
};

/**
 * Screen time, in the largest unit that keeps the number readable.
 *
 * Play logs accumulate fast, so a campaign's total is usually hours — showing
 * it in seconds would be a number nobody can read at a glance.
 */
export const formatPlayDuration = (seconds: number): string => {
  if (!seconds) return "0 sec";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) return `${hours} hr ${minutes} min`;
  if (minutes > 0) return `${minutes} min ${secs} sec`;
  return `${secs} sec`;
};
